<script setup lang="ts">
import { computed, useSlots } from "vue";

import ChipRemoveButton from "~/components/dumb/ChipRemoveButton.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { CheckIcon, OpenInternalLinkIcon, SwapIcon } from "~/icons";
import { CommandId } from "~/shared/enums";
import { usePageStore } from "~/stores";

const props = defineProps<{
  id?: string;
  selected?: boolean;
  noPadding?: boolean;
  commandId?: CommandId;
  replaceEnabled?: boolean;
  openEnabled?: boolean;
  linkIndicatorEnabled?: boolean;
  navigateEnabled?: boolean;
  navigateTooltip?: string;
  removeEnabled?: boolean;
}>();

const emit = defineEmits<{
  click: [event: Event];
  replace: [];
  open: [];
  navigate: [];
  remove: [];
}>();

const pageStore = usePageStore();
const slots = useSlots();

const paddingClasses = computed(() =>
  props.noPadding ? "" : pageStore.isMobile ? "py-2.5 pl-5 pr-4" : "py-1 pl-3 pr-2"
);
</script>

<template>
  <div
    :id="id"
    class="flex w-full items-center justify-between"
    :class="paddingClasses"
    @click="emit('click', $event)"
    @keydown.enter="emit('click', $event)">
    <slot />
    <slot name="actions">
      <div class="flex items-center">
        <Tooltip v-if="replaceEnabled && !selected" :command-id="commandId ?? CommandId.SWAP" class="ml-1">
          <div
            class="flex icon-md items-center justify-center rounded-sm opacity-0 hover:bg-hvy [[data-focused]_&]:opacity-100"
            @click.stop="emit('replace')"
            @keydown.enter.stop="emit('replace')">
            <SwapIcon class="icon-xs cursor-pointer text-md" />
          </div>
        </Tooltip>
        <Tooltip v-if="navigateEnabled" :text="navigateTooltip" class="ml-1">
          <div
            class="flex icon-md items-center justify-center rounded-sm opacity-0 hover:bg-hvy [[data-focused]_&]:opacity-100"
            @click.stop="emit('navigate')"
            @keydown.enter.stop="emit('navigate')">
            <OpenInternalLinkIcon class="icon-xs cursor-pointer text-md" />
          </div>
        </Tooltip>
        <slot name="extraActions" />
        <div
          v-if="openEnabled && selected"
          class="mx-1 flex items-center justify-center rounded-sm hover:bg-hvy"
          @click.stop="emit('open')"
          @keydown.enter.stop="emit('open')">
          <OpenInternalLinkIcon class="cursor-pointer text-md" :class="pageStore.isMobile ? 'icon-md' : 'icon-xs'" />
        </div>
        <OpenInternalLinkIcon
          v-if="linkIndicatorEnabled"
          class="ml-1 shrink-0 text-vlt"
          :class="pageStore.isMobile ? 'icon-md' : 'icon-xs'" />
        <ChipRemoveButton v-if="removeEnabled" :selected="selected" @remove="emit('remove')" />
        <CheckIcon v-if="!slots.actions && selected && pageStore.isMobile" class="ml-2 icon-md text-primary-base" />
      </div>
    </slot>
  </div>
</template>
